import { CustomSelect } from "./CustomSelect";
import React, { useState, useEffect } from "react";
import { cardStyle } from "../../../helpers/Executive-Dashboard/styleFormat";
import { 
    ResponsiveContainer, AreaChart,CartesianGrid, XAxis, YAxis, Tooltip, Area,
    LabelList 
} from "recharts";
import { CustomizedLabel } from "./CustomizedLabel";
import { fetchRevenueTrend } from '../../../services/salesInvoiceService';
import { fmtCompact, fmtNum, fmt } from "../../../helpers/Executive-Dashboard/formatters";
import { tooltipStyle } from "../../../helpers/Executive-Dashboard/styleFormat";
import { integration } from "../../../helpers/Executive-Dashboard/integration";

export const RevenueTrendCard = React.memo(({ src, index, multi, branch }) => {
    const [trendPeriod, setTrendPeriod] = useState('daily');
    const now = new Date();
    const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth(); // previous month (1-indexed)
    const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const [selectedMonth, setSelectedMonth] = useState(prevMonth);
    const [selectedYear, setSelectedYear] = useState(prevYear);
    const [trendData, setTrendData] = useState(src.revenueTrend || []);
    const [trendLoading, setTrendLoading] = useState(false);

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const currentYear = now.getFullYear();
    const yearsOptions = Array.from({ length: currentYear - 2015 + 1 }, (_, i) => {
        const y = currentYear - i;
        return { value: y, label: y.toString() };
    });

    const periodOptions = [
        { value: 'daily', label: 'Daily' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' }
    ];

    useEffect(() => {
        const loadTrend = async () => {
            setTrendLoading(true);
            try {
                const config = src.label === 'Gelora Printing' ? integration('GP') : integration('SUMA');
                const result = await fetchRevenueTrend(trendPeriod, selectedMonth, selectedYear, config, branch);
                setTrendData(result);
            } catch (err) {
                console.error('Revenue trend error:', err);
                setTrendData([]);
            } finally {
                setTrendLoading(false);
            }
        };
        loadTrend();
    }, [trendPeriod, selectedMonth, selectedYear, src.label, branch]);

    return (
        <div style={{ ...cardStyle, marginBottom: '16px', borderTop: '3px solid #10b981' }}>
            <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span>Revenue Trend {multi ? (<span style={{ color: '#94a3b8' }}>{`— ${src.label}`}</span>) : ''}</span>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <CustomSelect 
                                value={trendPeriod}
                                options={periodOptions}
                                onChange={(val) => setTrendPeriod(val)}
                                minWidth="100px"
                            />

                            {trendPeriod === 'daily' && (
                                <CustomSelect 
                                    value={selectedMonth}
                                    options={months}
                                    onChange={(val) => setSelectedMonth(Number(val))}
                                    minWidth="120px"
                                />
                            )}

                            {(trendPeriod === 'daily' || trendPeriod === 'monthly') && (
                                <CustomSelect 
                                    value={selectedYear}
                                    options={yearsOptions}
                                    onChange={(val) => setSelectedYear(Number(val))}
                                    minWidth="90px"
                                />
                            )}
                        </div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Revenue trend berdasarkan filter yang dipilih</div>
                </div>
            </div>
            <div style={{ height: '200px' }}>
                {trendLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>Loading...</div>
                ) : (trendData === null || trendData.length === 0) ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>Tidak ada data untuk periode ini</div>
                ) : (
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{ top: 45, right: 30, bottom: 0, left: 0 }}>
                        <defs>
                            <linearGradient id={`gradRevenue-${index}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis width={65} tickFormatter={(v) => fmtCompact(v)} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (!active || !payload || !payload.length) return null;
                                const d = payload[0].payload;
                                return (
                                    <div style={{ ...tooltipStyle, padding: '10px 14px' }}>
                                        <div style={{ fontWeight: 600, marginBottom: '6px', color: '#334155' }}>Tanggal: {label}</div>
                                        <div style={{ color: '#64748b', fontSize: '11px' }}>Jumlah Invoice: {fmtNum(d.count || 0)}</div>
                                        <div style={{ color: '#10b981', fontWeight: 600, marginTop: '4px' }}>Revenue: {fmt(d.total)}</div>
                                    </div>
                                );
                            }}
                        />
                        <Area type="monotone" dataKey="total" name="Revenue" stroke="#10b981" strokeWidth={2.5}
                            fill={`url(#gradRevenue-${index})`}
                            dot={{ r: 3, strokeWidth: 2, stroke: '#fff', fill: '#10b981' }}
                            activeDot={{ r: 5, stroke: '#10b981', strokeWidth: 2 }}
                        >
                            <LabelList dataKey="total" position="top" content={<CustomizedLabel />} />
                        </Area>
                    </AreaChart>
                </ResponsiveContainer>
                )}
            </div>
        </div>
    );
});